import { createContext, useContext, useReducer, useCallback } from 'react'

const INITIAL_FORM_DATA = {
  // Step 1 – Applicant
  name_applicant: '',
  relation_applicant: '',
  phone_primary: '',
  phone_secondary: '',
  area_applicant: '',
  // Step 2 – Martyr basic
  martyr_fullname: '',
  mother_name: '',
  birth_date: '',
  birth_date_accuracy: 'دقيق',
  current_residence: '',
  civil_registry_place: '',
  marital_status: '',
  photo_publish_consent: '',
  // Step 3 – Incident
  incident_type: '',
  incidentDetails: {},
  evidence_types: [],
  // Step 4 – Witnesses
  has_witnesses: '',
  witnesses: [],
  // Step 5 – Family
  spouseExists: '',
  spouse_fullname: '',
  spouse_birth_year: '',
  spouse_current_location: '',
  spouse_country: '',
  children: [],
  alternative_provider_exists: '',
  family_support_needs: [],
  // Step 5 – Contact
  case_contact_person: '',
  case_contact_relation: '',
  contact_primary_phone: '',
  contact_secondary_phone: '',
  contact_whatsapp: '',
  contact_best_time: '',
  contact_for_verification_consent: '',
  contact_event_notifications: '',
  // Step 6 – Verification
  region: '',
  neighborhood: '',
  affiliationDeclaration: false,
  allow_official_verification: '',
  final_signature: '',
  final_date: '',
}

const INITIAL_STATE = {
  currentStep: 1,
  formData: INITIAL_FORM_DATA,
  files: {
    martyr_photo_upload: null,
    evidence_upload: [],
  },
  errors: {},
  submissionStatus: 'idle', // idle | submitting | submitted | error | queued
  submittedCase: null,
}

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload, errors: {} }

    case 'UPDATE_FIELD':
      return {
        ...state,
        formData: { ...state.formData, [action.field]: action.value },
        errors: { ...state.errors, [action.field]: undefined },
      }

    case 'UPDATE_INCIDENT_DETAIL':
      return {
        ...state,
        formData: {
          ...state.formData,
          incidentDetails: { ...state.formData.incidentDetails, [action.field]: action.value },
        },
      }

    case 'SET_FILE':
      return {
        ...state,
        files: { ...state.files, martyr_photo_upload: action.payload },
      }

    case 'ADD_EVIDENCE_FILE':
      return {
        ...state,
        files: {
          ...state.files,
          evidence_upload: [...state.files.evidence_upload, ...action.payload],
        },
      }

    case 'REMOVE_EVIDENCE_FILE':
      return {
        ...state,
        files: {
          ...state.files,
          evidence_upload: state.files.evidence_upload.filter((_, i) => i !== action.index),
        },
      }

    case 'ADD_WITNESS':
      if (state.formData.witnesses.length >= 5) return state
      return {
        ...state,
        formData: {
          ...state.formData,
          witnesses: [
            ...state.formData.witnesses,
            { witness_name: '', witness_phone: '', witness_role: '', witness_contact_consent: '', witness_notes: '' },
          ],
        },
      }

    case 'UPDATE_WITNESS':
      return {
        ...state,
        formData: {
          ...state.formData,
          witnesses: state.formData.witnesses.map((w, i) =>
            i === action.index ? { ...w, [action.field]: action.value } : w
          ),
        },
      }

    case 'REMOVE_WITNESS':
      return {
        ...state,
        formData: {
          ...state.formData,
          witnesses: state.formData.witnesses.filter((_, i) => i !== action.index),
        },
      }

    case 'ADD_CHILD':
      return {
        ...state,
        formData: {
          ...state.formData,
          children: [
            ...state.formData.children,
            { child_name: '', child_birth_date: '', child_location: '', child_has_provider: '', child_support_needed: '' },
          ],
        },
      }

    case 'UPDATE_CHILD':
      return {
        ...state,
        formData: {
          ...state.formData,
          children: state.formData.children.map((c, i) =>
            i === action.index ? { ...c, [action.field]: action.value } : c
          ),
        },
      }

    case 'REMOVE_CHILD':
      return {
        ...state,
        formData: {
          ...state.formData,
          children: state.formData.children.filter((_, i) => i !== action.index),
        },
      }

    case 'TOGGLE_ARRAY_VALUE': {
      const arr = state.formData[action.field] || []
      const updated = arr.includes(action.value)
        ? arr.filter((v) => v !== action.value)
        : [...arr, action.value]
      return {
        ...state,
        formData: { ...state.formData, [action.field]: updated },
        errors: { ...state.errors, [action.field]: undefined },
      }
    }

    case 'SET_ERRORS':
      return { ...state, errors: action.payload }

    case 'SET_SUBMISSION_STATUS':
      return { ...state, submissionStatus: action.payload }

    case 'SET_SUBMITTED_CASE':
      return { ...state, submittedCase: action.payload, submissionStatus: 'submitted' }

    case 'LOAD_DRAFT':
      return {
        ...state,
        formData: { ...INITIAL_FORM_DATA, ...action.payload },
        currentStep: action.step || 1,
        errors: {},
      }

    case 'RESET':
      return { ...INITIAL_STATE }

    default:
      return state
  }
}

const FormContext = createContext(null)

export function FormProvider({ children }) {
  const [state, dispatch] = useReducer(formReducer, INITIAL_STATE)

  const setStep = useCallback((step) => dispatch({ type: 'SET_STEP', payload: step }), [])
  const updateField = useCallback(
    (field, value) => dispatch({ type: 'UPDATE_FIELD', field, value }),
    []
  )
  const updateIncidentDetail = useCallback(
    (field, value) => dispatch({ type: 'UPDATE_INCIDENT_DETAIL', field, value }),
    []
  )
  const setFile = useCallback((file) => dispatch({ type: 'SET_FILE', payload: file }), [])
  const addEvidenceFiles = useCallback(
    (files) => dispatch({ type: 'ADD_EVIDENCE_FILE', payload: files }),
    []
  )
  const removeEvidenceFile = useCallback(
    (index) => dispatch({ type: 'REMOVE_EVIDENCE_FILE', index }),
    []
  )
  const addWitness = useCallback(() => dispatch({ type: 'ADD_WITNESS' }), [])
  const updateWitness = useCallback(
    (index, field, value) => dispatch({ type: 'UPDATE_WITNESS', index, field, value }),
    []
  )
  const removeWitness = useCallback(
    (index) => dispatch({ type: 'REMOVE_WITNESS', index }),
    []
  )
  const addChild = useCallback(() => dispatch({ type: 'ADD_CHILD' }), [])
  const updateChild = useCallback(
    (index, field, value) => dispatch({ type: 'UPDATE_CHILD', index, field, value }),
    []
  )
  const removeChild = useCallback(
    (index) => dispatch({ type: 'REMOVE_CHILD', index }),
    []
  )
  const toggleArrayValue = useCallback(
    (field, value) => dispatch({ type: 'TOGGLE_ARRAY_VALUE', field, value }),
    []
  )
  const setErrors = useCallback((errors) => dispatch({ type: 'SET_ERRORS', payload: errors }), [])
  const setSubmissionStatus = useCallback(
    (status) => dispatch({ type: 'SET_SUBMISSION_STATUS', payload: status }),
    []
  )
  const setSubmittedCase = useCallback(
    (caseData) => dispatch({ type: 'SET_SUBMITTED_CASE', payload: caseData }),
    []
  )
  const loadDraft = useCallback(
    (data, step) => dispatch({ type: 'LOAD_DRAFT', payload: data, step }),
    []
  )
  const resetForm = useCallback(() => dispatch({ type: 'RESET' }), [])

  return (
    <FormContext.Provider
      value={{
        state,
        setStep,
        updateField,
        updateIncidentDetail,
        setFile,
        addEvidenceFiles,
        removeEvidenceFile,
        addWitness,
        updateWitness,
        removeWitness,
        addChild,
        updateChild,
        removeChild,
        toggleArrayValue,
        setErrors,
        setSubmissionStatus,
        setSubmittedCase,
        loadDraft,
        resetForm,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}

export function useForm() {
  const ctx = useContext(FormContext)
  if (!ctx) throw new Error('useForm must be used inside FormProvider')
  return ctx
}
